import { useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Spin, Button as AntdButton } from 'antd';
import dayjs from 'dayjs';
import { SortAscendingOutlined, SortDescendingOutlined } from '@ant-design/icons';
import EnhancedTable from 'app/molecules/EnhancedTable';
import { EnhancedColumnType } from 'app/molecules/EnhancedTable/types';
import { NavBreadcrumbContext } from 'app/molecules/NavBreadcrumb/NavBreadcrumbContext';
import { getUsersList } from 'app/modules/UserManagement/service';
import { getAuditLogs } from './service';
import { AuditActionLabelMap } from './constants';
import { AuditLogEntry } from './types';

const LogsManagement = () => {
  const [sort, setSort] = useState<'asc' | 'desc'>('desc');
  const { setItems } = useContext(NavBreadcrumbContext);

  useEffect(() => {
    setItems([{ title: 'Dashboard', path: '/' }, { title: 'Activity Logs' }]);
  }, [setItems]);

  const { data: logs = [], isLoading } = useQuery({
    queryKey: ['audit-logs', sort],
    queryFn: () => getAuditLogs(sort),
  });

  // This page is admin-only, same as User Management, so it's safe to reuse
  // the users list here purely to resolve a friendly name/email instead of
  // showing raw actor UUIDs.
  const { data: users = [] } = useQuery({
    queryKey: ['users-list'],
    queryFn: getUsersList,
  });

  const userLookup = useMemo(() => new Map(users.map((user) => [user.id, user])), [users]);

  const columns: EnhancedColumnType<AuditLogEntry>[] = [
    {
      title: 'Actor',
      dataIndex: 'actorId',
      key: 'actorId',
      render: (actorId: string) => {
        const user = userLookup.get(actorId);
        return user ? user.displayName || user.email : actorId;
      },
      className: 'font-figtree',
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (action: AuditLogEntry['action']) => AuditActionLabelMap[action] ?? action,
      className: 'font-figtree',
    },
    {
      title: 'Item ID',
      dataIndex: 'itemId',
      key: 'itemId',
      render: (itemId?: string) => itemId ?? '-',
      className: 'font-figtree',
    },
    {
      title: 'Date & Time',
      dataIndex: 'occurredAt',
      key: 'occurredAt',
      render: (value: string) => dayjs(value).format('MMM D, YYYY HH:mm'),
      className: 'font-figtree whitespace-nowrap',
    },
  ];

  return (
    <Spin spinning={isLoading}>
      <div className="mx-auto max-2xl:max-w-[calc(100vw-180px)] max-sm:max-w-[calc(100vw-120px)] max-sm:px-0">
        <div className="mb-6 flex justify-between items-center gap-4">
          <h2 className="text-xl font-semibold font-figtree">Activity Logs</h2>
          <AntdButton
            onClick={() => setSort((prev) => (prev === 'desc' ? 'asc' : 'desc'))}
            icon={sort === 'desc' ? <SortDescendingOutlined /> : <SortAscendingOutlined />}
          >
            {sort === 'desc' ? 'Newest first' : 'Oldest first'}
          </AntdButton>
        </div>

        <div className="border rounded-lg overflow-x-auto">
          <EnhancedTable
            columns={columns}
            dataSource={logs}
            rowKey="id"
            onFilter={() => {}}
            showEdit={false}
            showView={false}
            showRemove={false}
            className="font-figtree"
          />
        </div>
      </div>
    </Spin>
  );
};

export default LogsManagement;
